/**
 * Rw dan RO dibaca dari perpanjangan garis Vsh ke batuan bersih.
 *
 * RWA = RT / F adalah Rw yang *akan* terbaca seandainya seluruh pori berisi
 * air. Di kaki air ia mendekati Rw yang sebenarnya — tetapi tidak sama
 * dengannya, dan selisihnya bukan derau: lempung menghantar, jadi makin kotor
 * batuannya makin rendah RWA terbaca. Itulah kemiringan garis di sini, dan Rw
 * adalah tempat garis itu tiba di Vsh = 0, yaitu batuan tanpa lempung sama
 * sekali.
 *
 * Dua crossplot, dan keduanya sengaja tidak dihitung dari satu sama lain:
 *
 *     Vsh ─ RWA   →  Rw    perpotongan di Vsh = 0
 *     Vsh ─ RT    →  RO    perpotongan di Vsh = 0, tanpa lewat F
 *
 * RO di sebelah kanan tidak diturunkan dari Rw di sebelah kiri. Kalau
 * keduanya sepakat — RO ≈ F · Rw — itu pemeriksaan yang berarti; dua jalan
 * yang salah satunya dihitung dari yang lain selalu sepakat, dan tidak pernah
 * memberi tahu apa pun.
 *
 * **Sumbu tegaknya logaritmik, dan regresinya juga.** Resistivitas membentang
 * beberapa dekade, dan kuadrat terkecil pada skala linear dikuasai segelintir
 * sampel terbesarnya. Garis lurus di layar ini karena itu benar-benar garis
 * yang dipasang, bukan lengkung yang diluruskan gambarnya.
 *
 * Titik yang tidak masuk regresi tetap digambar, pudar dan kelabu. Sebuah
 * crossplot yang hanya memperlihatkan titik yang dipakainya tidak memberi cara
 * apa pun melihat bahwa kaki airnya salah dipilih.
 *
 * **Garisnya boleh digeser dengan tangan.** Kuadrat terkecil menjawab satu
 * pertanyaan — garis mana yang paling sedikit melesetkan *seluruh* titik yang
 * diberikan padanya — dan itu tidak selalu pertanyaan yang sedang ditanyakan
 * orang. Kaki air sebuah lapangan kerap memuat dua rombongan: pasir bersih
 * yang benar-benar berair, dan lapisan kotor yang lolos penggolong. Yang
 * pertama yang menentukan Rw; yang kedua menariknya turun. Mata yang mengenali
 * rombongan mana yang mana tidak punya cara mengatakannya kepada `polyfit`,
 * jadi garisnya dibuat bisa dipegang: pegangan kiri menggeser perpotongan di
 * Vsh = 0 — yaitu Rw atau RO itu sendiri — dan pegangan kanan memutar
 * kemiringannya tanpa melepaskan tambatan itu.
 *
 * Garis kuadrat terkecil tetap tergambar di bawahnya, putus-putus, dan sisa
 * kuadrat keduanya dipajang berdampingan atas himpunan titik yang sama persis.
 * Sebuah garis tangan tanpa pembanding adalah garis yang selalu terlihat
 * benar, dan yang menggambarnya tidak punya cara tahu berapa yang dilepasnya.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { Button, Empty, Loading, Notice, NumberField, Toggle } from "./ui";
import { decadeBounds, decadeTicks, logPlace } from "../lib/rocktype";
import {
  type Line,
  lineOf,
  misfit,
  pivotThrough,
  valueAtX,
  valueOf,
  withSlope,
  withValue,
} from "../lib/lrlcplot";
import type { LrlcCatalogue, LrlcFit, LrlcRwPayload, Screening, Well }
  from "../types";
import LrlcRwScope, { type RwFollowing, type RwScope } from "./LrlcRwScope";

/** Bingkai gambar, sama dengan crossplot RT–PHIE supaya keduanya sepadan. */
const PAD = 3;
const SPAN = 100 - PAD * 2;

/** Apa yang diminta crossplot ini, dari setelan yang sedang terpasang. */
export interface LrlcRwRequest {
  curves: Record<string, string>;
  cutoffs: Record<string, number>;
  archie: Record<string, number | null>;
  /**
   * Sumur mana dan kedalaman mana, sudah dalam ejaan yang dimengerti server.
   *
   * Dikirim sebagai badan yang sudah jadi dan bukan sebagai lingkup yang harus
   * diterjemahkan di sini: yang menerjemahkannya adalah `rwScopeBody`, satu
   * tempat, dan sebuah komponen gambar yang ikut memutuskan "sumur kosong
   * berarti seluruh proyek" adalah tempat kedua yang bisa berselisih dengannya.
   */
  scope: Record<string, unknown>;
}

/**
 * Sumbu Vsh: selalu memuat nol, dan mengikuti titik yang **diregresi**.
 *
 * Nol wajib ada sebab di nol itulah jawabannya dibaca. Ujung kanannya
 * mengikuti kaki air dan bukan seluruh sampel, dan itu bukan soal selera:
 * kaki air sebuah lapangan bisa seluruhnya berada di bawah Vsh 0,2 sementara
 * serpih di sekitarnya menjangkau 1,0. Sumbu yang memuat keduanya memampatkan
 * seluruh data yang dipasangi garis ke dalam seperlima kiri gambar, dan
 * kemiringan yang justru sedang dibaca orang menjadi tak terbaca.
 *
 * Diberi ruang setengah lagi di kanan titik terjauh, supaya garisnya tidak
 * berhenti tepat di titik terakhir, dan sekurang-kurangnya sampai 0,25 supaya
 * kaki air yang sangat bersih tidak melahirkan sumbu selebar dua senti.
 * Titik di luar jendela itu terpotong — ia latar, dan latar yang memaksa
 * gambarnya tak terbaca bukan latar yang menolong.
 */
function vshAxis(values: number[], used: boolean[]): [number, number] {
  const fitted = values.filter((v, i) => used[i] && Number.isFinite(v));
  const top = Math.max(0.1, ...(fitted.length ? fitted : values.filter(Number.isFinite)));
  return [0, Math.min(1, Math.max(0.25, Math.ceil(top * 1.5 * 20) / 20))];
}

function fmt(value: number, digits = 4): string {
  if (!Number.isFinite(value)) return "—";
  return value >= 100 ? value.toFixed(0)
    : value >= 1 ? value.toFixed(2)
    : value.toFixed(digits);
}

export default function LrlcRwCrossplot({
  project,
  request,
  onUseRw,
  scope,
  onScopeChange,
  wells,
  field,
  following,
}: {
  project: { id: number };
  request: LrlcRwRequest;
  /** Memasang taksirannya sebagai Rw yang dipakai layar LRLC. */
  onUseRw?: (rw: number) => void;
  /** Lingkup yang sedang terpasang, dan cara menggesernya. */
  scope: RwScope;
  onScopeChange: (next: RwScope) => void;
  /** Sumur yang sudah dibor, sebagaimana dikenal katalog LRLC. */
  wells: LrlcCatalogue["wells"];
  /** Sumur lapangan berikut marker dan zonanya, untuk pemilih kedalamannya. */
  field: Well[];
  /** Apa yang diikuti pemilihnya selama belum disentuh. */
  following: RwFollowing;
}) {
  const body = useMemo(() => ({
    ...request.curves,
    ...request.cutoffs,
    ...Object.fromEntries(
      Object.entries(request.archie).filter(([, v]) => v !== null && v !== undefined)),
    ...request.scope,
  }), [request]);

  const run = useAsync(
    () => api.post<LrlcRwPayload>(`/api/projects/${project.id}/lrlc/rw/`, body),
    [project.id, JSON.stringify(body)],
  );
  const data = run.data;

  /**
   * Garis yang digeser dengan tangan, satu untuk tiap crossplot.
   *
   * `null` berarti "pakai garis kuadrat terkecil dari server", dan itu keadaan
   * awalnya: yang dipajang sebelum siapa pun menyentuhnya harus tetap jawaban
   * hitungan, bukan tebakan yang kebetulan tertinggal.
   *
   * Dilepas setiap kali muatan baru mendarat, dan itu bukan kerapian. Sebuah
   * garis tangan digambar **pada sebaran tertentu**; begitu sumur, selang atau
   * ambangnya digeser, kaki airnya berpindah dan garis yang sama berdiri di
   * atas titik-titik yang bukan lagi titik-titik yang dilihat orang ketika
   * menggambarnya. Yang paling sering memicunya justru tombol "Use … as Rw"
   * di sini sendiri: memasang Rw menggeser penggolongan, dan penggolongan yang
   * bergeser memindahkan sampel mana yang disebut air.
   */
  const [manual, setManual] = useState<{ rw: Line | null; ro: Line | null }>(
    { rw: null, ro: null });
  useEffect(() => setManual({ rw: null, ro: null }), [data]);

  /**
   * Rw yang sedang berlaku di layar ini: dari tangan bila ada, dari regresi
   * bila tidak.
   *
   * Dibaca dari `fit.value` dan bukan dihitung ulang sebagai `10^intercept`
   * selama belum disentuh — keduanya angka yang sama, dan yang dipajang
   * sebaiknya angka yang benar-benar dipulangkan server.
   */
  const rwValue = manual.rw ? valueOf(manual.rw) : data?.rw?.value ?? null;

  /**
   * Hasil jaring, dari jawaban yang berhasil **atau** dari penolakannya.
   *
   * Kedua penolakan jaring — tidak satu pun lolos, dan terlalu banyak yang
   * lolos — menyuruh orang menggeser radiusnya, dan yang dibutuhkan untuk
   * menggesernya dengan benar adalah tabel dan peta yang memperlihatkan siapa
   * yang tertangkap pada radius sekarang. Server mengirimkannya bersama
   * penolakan itu; membuangnya di sini berarti menutup peta yang tepat sedang
   * menjawab pertanyaan yang ditimbulkan penolakannya sendiri.
   */
  const screening = useMemo(() => {
    // Jawaban lama tidak boleh berdiri di bawah galat yang baru.
    //
    // `useAsync` menyimpan jawaban terakhir yang berhasil supaya permintaan
    // berikutnya tidak mengosongkan layar selama ia berjalan — dan itu benar
    // selama ia *sedang berjalan*. Begitu yang baru ditolak, yang tersimpan
    // menjadi peta lingkaran 5 km yang berdiri tenang di samping kalimat
    // tentang lingkaran 100 km, dengan angka di kotak radius mengatakan hal
    // ketiga lagi. Yang membacanya tidak punya cara tahu mana yang sedang
    // dijawab.
    if (run.error) {
      const body = run.errorBody;
      if (body && typeof body === "object" && "screening" in body) {
        return (body as { screening: Screening | null }).screening;
      }
      return null;
    }
    return data?.screening ?? null;
  }, [data, run.error, run.errorBody]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Pemilih datanya berdiri lebih dulu dan **selalu** tergambar, juga
          ketika hitungannya sedang berjalan atau ditolak. Sebuah panel yang
          lenyap bersama galatnya menyuruh orang membetulkan pilihan yang
          sedang tidak bisa disentuhnya — dan justru galat di sini yang paling
          sering berbunyi "persempit radiusnya". */}
      <LrlcRwScope
        scope={scope}
        onChange={onScopeChange}
        wells={wells}
        field={field}
        following={following}
        screening={screening}
      />

      {run.loading && !data && <Loading label="Reading the water legs…" />}
      {run.error && <Notice tone="error">{run.error}</Notice>}

      {/* Gambarnya padam bersama galatnya, dengan alasan yang sama seperti
          jaringnya: sebuah crossplot yang tetap memajang Rw dari permintaan
          sebelumnya di bawah kalimat yang mengatakan permintaan ini ditolak
          adalah satu angka yang terlihat sama meyakinkannya — dan tidak ada
          apa pun di layar yang mengatakan bahwa ia menjawab pertanyaan yang
          lain. Yang tetap berdiri adalah pemilihnya, sebab di situlah
          galatnya dibetulkan. */}
      {data && !run.error && (
        <>
          <Notice tone="info">{data.note}</Notice>

          {/* Apa yang benar-benar terbaca, di atas gambarnya. Sebuah
              perpotongan adalah satu angka, dan satu angka terlihat sama
              meyakinkannya entah ia datang dari satu sumur pada seluruh
              kedalamannya atau dari dua puluh sumur di dalam satu zona. */}
          <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
            Read from {data.wells.length.toLocaleString("en-US")}{" "}
            {data.wells.length === 1 ? "well" : "wells"} · {data.interval} ·{" "}
            {data.points.water.toLocaleString("en-US")} water samples of{" "}
            {data.points.count.toLocaleString("en-US")}
            {data.screening
              ? ` · ${data.screening.kept} of ${data.screening.asked} caught within `
                + `${(data.screening.radius_m / 1000).toFixed(2).replace(/\.?0+$/, "")} km `
                + `of ${data.screening.center_name}`
              : ""}
          </span>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <Plot title="Vsh — RWA" unit="RWA ohm·m · log"
                  asks="Where the water legs reach clay-free rock is Rw"
                  vsh={data.points.vsh} y={data.points.rwa} used={data.points.used}
                  wells={data.points.well} palette={data.wells.map((w) => w.color)}
                  fit={data.rw} label="Rw"
                  manual={manual.rw}
                  onManual={(line) => setManual((prev) => ({ ...prev, rw: line }))}
                  compare={{ label: "Rw now set", value: data.archie.rw }}
                  minSamples={data.min_samples}
                  action={rwValue !== null && onUseRw
                    ? { label: `Use ${fmt(rwValue)} as Rw`,
                        onClick: () => onUseRw(rwValue) }
                    : undefined} />

            {/* Pembandingnya memakai Rw yang **sedang berlaku** di sebelah
                kiri, termasuk yang barusan digeser dengan tangan. Sebuah
                pemeriksaan silang yang diam-diam tetap memakai angka kuadrat
                terkecil sementara yang dipajang di sebelahnya sudah lain
                adalah pemeriksaan atas dua Rw yang berbeda — dan ia akan
                mengatakan "sepakat" pada saat yang paling tidak boleh. */}
            <Plot title="Vsh — RT" unit="RT ohm·m · log"
                  asks="The same question asked of RT directly, without F"
                  vsh={data.points.vsh} y={data.points.rt} used={data.points.used}
                  wells={data.points.well} palette={data.wells.map((w) => w.color)}
                  fit={data.ro} label="RO"
                  manual={manual.ro}
                  onManual={(line) => setManual((prev) => ({ ...prev, ro: line }))}
                  compare={rwValue !== null && data.f_mean
                    ? { label: "F̄ · Rw from the left",
                        value: rwValue * data.f_mean }
                    : undefined}
                  minSamples={data.min_samples} />
          </div>

          <WellTable data={data} />

          {data.skipped.length > 0 && (
            <Notice tone="warn">
              {data.skipped.map((s) => `${s.well}: ${s.reason}`).join(" · ")}
            </Notice>
          )}
        </>
      )}
    </div>
  );
}

function Plot({
  title, unit, asks, vsh, y, used, wells, palette, fit, label, compare,
  minSamples, action, manual, onManual,
}: {
  title: string;
  unit: string;
  asks: string;
  vsh: number[];
  y: number[];
  used: boolean[];
  wells: number[];
  palette: string[];
  fit: LrlcFit | null;
  label: string;
  compare?: { label: string; value: number };
  minSamples: number;
  action?: { label: string; onClick: () => void };
  /** Garis yang digeser dengan tangan, atau `null` selama belum disentuh. */
  manual: Line | null;
  onManual: (line: Line | null) => void;
}) {
  const xAxis = useMemo(() => vshAxis(vsh, used), [vsh, used]);
  /**
   * Sumbu tegak: dekade yang memuat kaki airnya — **dan garis tangannya**.
   *
   * Yang kedua bukan kemewahan. Sebuah Rw yang diketik di luar dekade datanya
   * menaruh garis dan pegangannya di luar kotak, dan yang terjadi kemudian
   * adalah pegangan yang tidak bisa disentuh lagi untuk menariknya kembali.
   */
  const yAxis = useMemo(() => {
    const fromData = decadeBounds(y.filter((_, i) => used[i]))
      ?? decadeBounds(y) ?? [0.01, 100];
    const held = manual ? valueOf(manual) : 0;
    if (!(held > 0)) return fromData;
    return [Math.min(fromData[0], 10 ** Math.floor(Math.log10(held))),
            Math.max(fromData[1], 10 ** Math.ceil(Math.log10(held)))];
  }, [y, used, manual]);
  const ticks = useMemo(() => decadeTicks(yAxis[0], yAxis[1]), [yAxis]);

  const place = (value: number) =>
    PAD + ((value - xAxis[0]) / (xAxis[1] - xAxis[0])) * SPAN;
  const height = (value: number) =>
    PAD + (1 - logPlace(value, yAxis[0], yAxis[1])) * SPAN;

  /**
   * Garis kuadrat terkecil, dan garis yang benar-benar dipajang.
   *
   * Tangan hanya boleh berangkat dari sebuah regresi yang ada. Kaki air yang
   * terlalu tipis untuk diregresi juga terlalu tipis untuk digambari garis
   * dengan tangan — `MIN_FIT_SAMPLES` ada justru supaya tidak ada yang
   * mempercayai sebuah garis lewat tiga titik, dan pintu belakang yang
   * membiarkannya masuk dengan tangan membuka kembali pintu yang sama.
   */
  const fitted = useMemo(() => (fit ? lineOf(fit) : null), [fit]);
  const line = manual ?? fitted;
  const value = manual ? valueOf(manual) : fit?.value ?? null;

  /** Sisa kuadrat kedua garis, atas himpunan titik yang sama persis. */
  const drift = useMemo(
    () => (manual ? misfit(vsh, y, used, manual) : null), [manual, vsh, y, used]);
  const settled = useMemo(
    () => (manual && fitted ? misfit(vsh, y, used, fitted) : null),
    [manual, fitted, vsh, y, used]);

  const box = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<null | "value" | "slope">(null);

  /**
   * Nilai sumbu tegak pada sebuah ordinat piksel di dalam kotaknya.
   *
   * Dijepit ke sumbunya sendiri, dan hanya di sini penjepitan itu benar:
   * tangan yang meluncur keluar dari kotak tidak sedang mengatakan "Rw seribu
   * ohm·m" melainkan "setinggi-tingginya", dan garis yang melesat ke luar
   * gambar membawa pegangannya pergi bersamanya.
   */
  const valueAtY = (clientY: number, rect: DOMRect): number => {
    if (!(rect.height > 0)) return NaN;
    const t = 1 - (((clientY - rect.top) / rect.height) * 100 - PAD) / SPAN;
    const lo = Math.log10(yAxis[0]);
    const hi = Math.log10(yAxis[1]);
    return 10 ** Math.min(hi, Math.max(lo, lo + t * (hi - lo)));
  };

  const startDrag = (kind: "value" | "slope") => (event: React.PointerEvent) => {
    event.preventDefault();
    setDragging(kind);
    box.current?.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !line) return;
    const at = valueAtY(event.clientY, event.currentTarget.getBoundingClientRect());
    if (!(at > 0)) return;
    // Pegangan kiri menggeser perpotongan; pegangan kanan memutar kemiringan
    // **tanpa melepaskan tambatan di Vsh = 0**, sebab perpotongan itulah
    // jawabannya dan ia tidak boleh ikut bergeser ketika yang disentuh adalah
    // ujung yang lain.
    const next = dragging === "value"
      ? withValue(line, at)
      : pivotThrough(line, xAxis[1], at);
    if (next) onManual(next);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(null);
    box.current?.releasePointerCapture?.(event.pointerId);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5,
                  flex: "1 1 380px", minWidth: 320 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ font: "600 11px var(--font-mono)", color: "var(--ink-2)" }}>
          {title}
        </span>
        <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)" }}>
          {asks}
        </span>
      </div>

      <div style={{ display: "flex", gap: 6, height: 280 }}>
        <div style={{ display: "flex", flexDirection: "column", width: 46, flex: "none" }}>
          <div style={{ position: "relative", flex: 1 }}>
            {ticks.map((tick) => (
              <span key={tick} style={{
                position: "absolute", right: 4, top: `${height(tick)}%`,
                transform: "translateY(-50%)",
                font: "400 9px var(--font-mono)", color: "var(--ink-5)",
              }}>
                {tick >= 1 ? tick.toLocaleString("en-US") : tick}
              </span>
            ))}
          </div>
          <div style={{ height: 16 }} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <div
            ref={box}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
              position: "relative", flex: 1,
              border: "1px solid var(--line)", borderRadius: 7,
              background: "var(--panel-inset)", overflow: "hidden",
              touchAction: manual ? "none" : undefined,
            }}
          >
            <svg viewBox="0 0 100 100" preserveAspectRatio="none"
                 style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
              {ticks.map((tick) => (
                <line key={tick} x1={PAD} y1={height(tick)} x2={PAD + SPAN} y2={height(tick)}
                      stroke="var(--line-soft)" strokeWidth={0.15} />
              ))}
              {/* Sumbu Vsh = 0 digambar tegas: di garis itulah jawabannya
                  dibaca, dan sebuah perpotongan tanpa sumbunya terlihat seperti
                  titik yang melayang. */}
              <line x1={place(0)} y1={PAD} x2={place(0)} y2={PAD + SPAN}
                    stroke="var(--line)" strokeWidth={0.3} />

              {/* Yang tidak dipakai digambar lebih dulu, supaya kaki air
                  berdiri di atasnya dan bukan tertimbun di bawahnya. */}
              {vsh.map((v, i) => used[i] ? null : (
                <circle key={`o${i}`} cx={place(v)} cy={height(y[i])} r={0.4}
                        fill="var(--ink-7)" opacity={0.18} />
              ))}
              {vsh.map((v, i) => !used[i] ? null : (
                <circle key={`u${i}`} cx={place(v)} cy={height(y[i])} r={0.5}
                        fill={palette[wells[i]] || "#1c7ed6"} opacity={0.85} />
              ))}

              {/* Garis kuadrat terkecil tetap tergambar di bawah garis tangan,
                  putus-putus. Sebuah garis tangan tanpa pembanding adalah
                  garis yang selalu terlihat benar. */}
              {manual && fitted && (
                <line
                  x1={place(xAxis[0])}
                  y1={height(valueAtX(fitted, xAxis[0]))}
                  x2={place(xAxis[1])}
                  y2={height(valueAtX(fitted, xAxis[1]))}
                  stroke="var(--ink-6)" strokeWidth={0.35} strokeDasharray="1.2 1.2" />
              )}
              {line && (
                <line
                  x1={place(xAxis[0])}
                  y1={height(valueAtX(line, xAxis[0]))}
                  x2={place(xAxis[1])}
                  y2={height(valueAtX(line, xAxis[1]))}
                  stroke="var(--accent)" strokeWidth={0.45} />
              )}
              {value !== null && (
                <circle cx={place(0)} cy={height(value)} r={1.1}
                        fill="none" stroke="var(--accent)" strokeWidth={0.5} />
              )}

              {/* Pegangannya hanya ada selagi garisnya memang sedang dipegang.
                  Dua bulatan yang selalu duduk di atas garis kuadrat terkecil
                  mengundang orang menyeretnya tanpa pernah mengatakan bahwa
                  yang tergeser adalah angka yang dipakai seluruh layar. */}
              {manual && line && (
                <>
                  <circle
                    aria-label={`Drag ${label} at Vsh = 0`}
                    cx={place(0)} cy={height(valueOf(line))} r={2}
                    fill="var(--accent)" stroke="var(--panel)" strokeWidth={0.4}
                    style={{ cursor: "ns-resize" }}
                    onPointerDown={startDrag("value")} />
                  <circle
                    aria-label={`Drag the ${label} slope`}
                    cx={place(xAxis[1])} cy={height(valueAtX(line, xAxis[1]))} r={2}
                    fill="var(--panel)" stroke="var(--accent)" strokeWidth={0.6}
                    style={{ cursor: "ns-resize" }}
                    onPointerDown={startDrag("slope")} />
                </>
              )}
            </svg>
          </div>

          <div style={{ position: "relative", height: 16, flex: "none" }}>
            {[0, 0.25, 0.5, 0.75, 1].map((f) => {
              const v = xAxis[0] + (xAxis[1] - xAxis[0]) * f;
              return (
                <span key={f} style={{
                  position: "absolute", left: `${place(v)}%`,
                  transform: "translateX(-50%)",
                  font: "400 9px var(--font-mono)", color: "var(--ink-5)",
                }}>
                  {v.toFixed(2)}
                </span>
              );
            })}
          </div>
          <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink-4)" }}>
            VSH v/v — {unit}
          </span>
        </div>
      </div>

      {fit && line && value !== null ? (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <span style={{ font: "600 12px var(--font-mono)", color: "var(--accent)" }}>
              {label} = {fmt(value)} ohm·m{manual ? " · by hand" : ""}
            </span>
            {/* Ketika garisnya digeser dengan tangan, R² dari server tidak lagi
                menerangkan garis yang tergambar — ia menerangkan garis yang
                lain. Yang menggantikannya adalah sisa kuadrat kedua garis atas
                himpunan titik yang sama persis: satu-satunya bentuk yang
                membuat keduanya bisa dibandingkan. */}
            <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
              {manual
                ? `misfit ${num(drift)} dec · least squares ${num(settled)} · `
                  + `slope ${line.slope.toFixed(2)} dec/Vsh`
                : `R² ${fit.r2.toFixed(3)} · ${fit.n.toLocaleString("en-US")} samples · `
                  + `slope ${fit.slope.toFixed(2)} dec/Vsh`}
            </span>
            {/* Sejauh apa perpotongannya diperpanjang dari data terdekatnya.
                Vsh terkecil 0,25 berarti seperempat sumbu ditempuh tanpa satu
                titik pun di bawahnya. Berlaku sama bagi garis tangan: yang
                diperpanjang adalah jarak dari data, bukan dari cara garisnya
                dipasang. */}
            <span style={{
              font: "400 9.5px var(--font-mono)",
              color: fit.x_min > 0.15 ? "var(--amber)" : "var(--ink-6)",
            }}>
              Vsh {fit.x_min.toFixed(2)}–{fit.x_max.toFixed(2)}
              {fit.x_min > 0.15 ? " — extrapolated to 0" : ""}
            </span>
            {compare && Number.isFinite(compare.value) && (
              <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                {compare.label} {fmt(compare.value)}
              </span>
            )}
            {action && (
              <Button variant="ghost" onClick={action.onClick}>{action.label}</Button>
            )}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
            <div style={{ width: 176, flex: "none" }}>
              <Toggle
                label={`Set ${label} by hand`}
                checked={manual !== null}
                onChange={(on) => onManual(on && fitted ? { ...fitted } : null)}
              />
            </div>
            {manual && (
              <>
                {/* Dibulatkan hanya untuk dipajang. Yang tersimpan tetap garis
                    yang digambar, sampai orang mengetik angka lain di sini. */}
                <NumberField
                  label={`${label} at Vsh = 0`}
                  unit="ohm·m"
                  value={Number(value.toPrecision(4))}
                  step={value / 10}
                  min={0}
                  onChange={(next) => {
                    const moved = withValue(manual, next);
                    if (moved) onManual(moved);
                  }}
                />
                <NumberField
                  label="Slope dec/Vsh"
                  value={Number(manual.slope.toFixed(3))}
                  step={0.05}
                  onChange={(next) => {
                    const turned = withSlope(manual, next);
                    if (turned) onManual(turned);
                  }}
                />
                <Button variant="ghost" onClick={() => onManual(fitted)}>
                  Back to least squares
                </Button>
              </>
            )}
            <span style={{ font: "400 9px/1.5 var(--font-sans)", color: "var(--ink-6)",
                           flex: "1 1 220px", minWidth: 180 }}>
              {manual
                ? "Drag the filled handle to move the intercept, the hollow one "
                  + "to turn the slope. The dashed line is what least squares "
                  + "answered."
                : "Least squares fits every water leg given to it, including the "
                  + "dirty ones that slipped through. Take the line by hand when "
                  + "the clean rock says otherwise."}
            </span>
          </div>
        </>
      ) : (
        <Empty>
          Not enough water to fit a line — {minSamples} samples are the least
          this will read. Move the RT/RO cutoff, or widen the interval.
        </Empty>
      )}
    </div>
  );
}

/** Sisa kuadrat yang mungkin tidak ada — tiga angka di belakang koma, atau strip. */
function num(value: number | null): string {
  return value === null || !Number.isFinite(value) ? "—" : value.toFixed(3);
}

/** Pembungkus tabel, sama dengan yang dipakai tabel lain di layar LRLC. */
function Table({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 7, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse",
                      background: "var(--panel-inset)" }}>
        {children}
      </table>
    </div>
  );
}

const head: React.CSSProperties = {
  padding: "5px 9px", textAlign: "right", whiteSpace: "nowrap",
  font: "500 9.5px var(--font-mono)", color: "var(--ink-5)",
  borderBottom: "1px solid var(--line)",
};
const cell: React.CSSProperties = {
  padding: "5px 9px", textAlign: "right", whiteSpace: "nowrap",
  font: "400 10.5px var(--font-mono)",
  borderBottom: "1px solid var(--line-soft)",
};

/** Satu baris per sumur: kaki airnya, dan Rw yang keluar dari kakinya sendiri. */
function WellTable({ data }: { data: LrlcRwPayload }) {
  return (
    <Table>
      <thead>
        <tr>
          <th style={{ ...head, textAlign: "left" }}>Well</th>
          <th style={head}>Samples</th>
          <th style={head}>Water</th>
          <th style={head}>Rw</th>
          <th style={head}>R²</th>
          <th style={head}>RO</th>
          <th style={head}>R²</th>
        </tr>
      </thead>
      <tbody>
        {data.wells.map((well) => (
          <tr key={well.id}>
            <td style={{ ...cell, textAlign: "left" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2,
                               background: well.color, flex: "none" }} />
                {well.name}
              </span>
            </td>
            <td style={cell}>{well.samples.toLocaleString("en-US")}</td>
            <td style={cell}>{well.water.toLocaleString("en-US")}</td>
            <td style={{ ...cell, color: well.rw ? "var(--accent)" : "var(--ink-6)" }}>
              {well.rw ? fmt(well.rw.value) : "—"}
            </td>
            <td style={cell}>{well.rw ? well.rw.r2.toFixed(3) : "—"}</td>
            <td style={cell}>{well.ro ? fmt(well.ro.value) : "—"}</td>
            <td style={cell}>{well.ro ? well.ro.r2.toFixed(3) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
